import { computed, ref, toRef, useHost, type ComputedRef, type Ref, type ToRefs } from "vue";
import type { InputUploadProps, FileValue } from "./inputUpload.types";
import type { Colors, Variants, Sizes } from "types";
import { useSharedState } from "composables/useSharedState";
import { useFormAssociated, type FormValue } from "composables/useFormAssociated";
import { useFieldInheritance } from "components/Field/useField";
import { createEventDispatcher } from "@/runtime/shared/events";
import { inputUploadClasses } from "./inputUpload.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { cardClasses } from "components/Card/card.styles";

export interface UseInputUploadOptions {
  props: Readonly<InputUploadProps>;
  fileInputRef: Ref<HTMLInputElement | null>;
}

export interface UseInputUploadReturn {
  state: ToRefs<InputUploadProps>;
  classes: ComputedRef<ReturnType<typeof inputUploadClasses>>;
  finalColor: ComputedRef<Colors>;
  finalVariant: ComputedRef<Exclude<Variants, "link" | "ghost">>;
  finalSize: ComputedRef<Sizes>;
  displayText: ComputedRef<string>;
  hasFiles: ComputedRef<boolean>;
  isButton: ComputedRef<boolean>;
  isDragActive: Ref<boolean>;
  fileList: ComputedRef<File[]>;
  invalidFiles: Ref<File[]>;
  openFilePicker: () => void;
  handleFileChange: (e: Event) => void;
  handleDragEnter: (e: DragEvent) => void;
  handleDragOver: (e: DragEvent) => void;
  handleDragLeave: (e: DragEvent) => void;
  handleDrop: (e: DragEvent) => void;
  removeFile: (file: File) => void;
  removeInvalidFile: (file: File) => void;
  formatFileSize: (bytes: number) => string;
  getFileIcon: (file: File) => string;
  validate: () => void;
  invalidFileCardCls: ReturnType<typeof cardClasses>;
  fileCardCls: ReturnType<typeof cardClasses>;
  getPreviewUrl: (file: File) => string | null;
}

/** Format bytes into a human-readable size string. */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / 1024 ** i;
  return `${i === 0 ? size : size.toFixed(1)} ${units[i]}`;
};

/** Extension-to-icon mapping for file types with dedicated icons. */
const FILE_ICON_MAP: Record<string, string> = {
  cs: "file-csharp",
  css: "file-css",
  csv: "file-csv",
  html: "file-html",
  htm: "file-html",
  java: "file-java",
  js: "file-javascript",
  mjs: "file-javascript",
  cjs: "file-javascript",
  json: "file-json",
  pdf: "file-pdf",
  svg: "file-svg",
  ts: "file-typescript",
  tsx: "file-typescript",
  vue: "file-vue",
  xml: "file-xml",
  xslt: "file-xslt",
  xsl: "file-xslt",
  yaml: "file-yaml",
  yml: "file-yaml",
};

/** Map a file to an icon name based on extension, then MIME fallback. */
const getFileIcon = (file: File): string => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext && FILE_ICON_MAP[ext]) return FILE_ICON_MAP[ext];
  if (file.type.startsWith("image/")) return "image";
  return "file";
};

/** Remove a file from the current selection and dispatch the update. */
const removeFile = (ctx: UploadContext, state: ToRefs<InputUploadProps>, file: File): void => {
  const current = state.modelValue?.value as FileValue;
  if (!current) return;

  if (current instanceof File) {
    dispatchUpdate(ctx, null);
    return;
  }

  if (Array.isArray(current)) {
    const remaining = current.filter((f) => f !== file);
    dispatchUpdate(ctx, remaining.length === 0 ? null : remaining);
  }
};

/** Convert selected files to a FormData value for form submission. */
const toFormValue = (value: unknown, name: string): FormValue => {
  if (value === undefined || value === null) return null;

  if (value instanceof File) {
    const formData = new FormData();
    formData.append(name, value);
    return formData;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const formData = new FormData();
    for (const file of value) formData.append(name, file);
    return formData;
  }

  return null;
};

/** Build display text from selected files. */
const getDisplayText = (modelValue: FileValue | undefined, placeholder?: string): string => {
  if (!modelValue) return placeholder ?? "";

  if (Array.isArray(modelValue)) {
    if (modelValue.length === 0) return placeholder ?? "";
    if (modelValue.length === 1) return modelValue[0].name;
    return `${modelValue.length} files selected`;
  }

  if (modelValue instanceof File) return modelValue.name;
  return placeholder ?? "";
};

/** Normalize current modelValue to a File array. */
const toFileArray = (modelValue: FileValue | undefined): File[] => {
  if (!modelValue) return [];
  if (Array.isArray(modelValue)) return modelValue;
  if (modelValue instanceof File) return [modelValue];
  return [];
};

/** Context for module-level event handlers. */
interface UploadContext {
  props: Readonly<InputUploadProps>;
  dispatchEvent: ReturnType<typeof createEventDispatcher>;
  validate: () => void;
  invalidFiles: Ref<File[]>;
}

/** Dispatch both modelValue and change events with the same value. */
const dispatchUpdate = (ctx: UploadContext, value: FileValue | null): void => {
  ctx.dispatchEvent("update:modelValue", value);
  ctx.dispatchEvent("change", value);
};

/** Store rejected files in context and dispatch a reject event. */
const rejectFiles = (ctx: UploadContext, rejected: File[]): void => {
  ctx.invalidFiles.value = rejected;
  ctx.dispatchEvent("reject", rejected);
};

/** Handle the native file input change event. */
const handleFileChange = (ctx: UploadContext, e: Event): void => {
  const input = e.target as HTMLInputElement;
  const files = input.files;

  ctx.invalidFiles.value = [];

  if (!files || files.length === 0) {
    dispatchUpdate(ctx, null);
    return;
  }

  const allFiles = [...files];
  const filtered = filterByAccept(allFiles, ctx.props.accept);
  const rejected = allFiles.filter((f) => !filtered.includes(f));

  if (rejected.length > 0) rejectFiles(ctx, rejected);
  if (filtered.length === 0) return;

  let value: File | File[];
  if (ctx.props.multiple) {
    const existing = toFileArray(ctx.props.modelValue);
    const newNames = new Set(filtered.map((f) => f.name));
    value = [...existing.filter((f) => !newNames.has(f.name)), ...filtered];
  } else {
    value = filtered[0];
  }
  dispatchUpdate(ctx, value);
  ctx.validate();

  // Reset the native input so re-selecting the same file triggers change
  input.value = "";
};

interface ClassesInput {
  finalColor: ComputedRef<Colors>;
  finalVariant: ComputedRef<Exclude<Variants, "link" | "ghost">>;
  finalSize: ComputedRef<Sizes>;
  disabled: ComputedRef<boolean>;
  block: ComputedRef<boolean>;
  isDragActive: Ref<boolean>;
  propClass: string | undefined;
}

/** Build computed classes for the drop zone. Button mode uses `<e-button>` directly. */
const setupClasses = (o: ClassesInput) =>
  computed(() =>
    mergeClassProp(
      inputUploadClasses({
        color: o.finalColor.value,
        variant: o.finalVariant.value,
        size: o.finalSize.value,
        disabled: o.disabled.value,
        block: o.block.value,
        dragActive: o.isDragActive.value,
      }),
      o.propClass,
    ),
  );

/** Computed display text from selected files. */
const createDisplayText = (state: ToRefs<InputUploadProps>, placeholder?: string) =>
  computed(() => getDisplayText(state.modelValue?.value as FileValue, placeholder));

/** Computed flag: are files currently selected? */
const createHasFiles = (state: ToRefs<InputUploadProps>) =>
  computed(() => {
    const value = state.modelValue?.value;
    if (!value) return false;
    return Array.isArray(value) ? value.length > 0 : value instanceof File;
  });

/** Computed file list normalizing modelValue to an array. */
const createFileList = (state: ToRefs<InputUploadProps>) =>
  computed<File[]>(() => {
    const value = state.modelValue?.value as FileValue;
    if (!value) return [];
    if (value instanceof File) return [value];
    if (Array.isArray(value)) return value;
    return [];
  });

/** Set up form association for file upload. */
const setupFormAssociated = (
  fileInputRef: Ref<HTMLInputElement | null>,
  state: ToRefs<InputUploadProps>,
  props: Readonly<InputUploadProps>,
) => {
  return useFormAssociated({
    inputRef: fileInputRef,
    modelValue: state.modelValue as Ref<FileValue | undefined>,
    disabled: toRef(props, "disabled"),
    required: toRef(props, "required"),
    toFormValue: (value) => toFormValue(value, state.name?.value ?? ""),
    disableEnterSubmit: true,
  });
};

/**
 * Filter dropped files by the `accept` attribute pattern.
 * Native `<input accept>` only constrains the file dialog, not drag-and-drop.
 */
const filterByAccept = (files: File[], accept: string | undefined): File[] => {
  if (!accept) return files;

  const patterns = accept.split(",").map((p) => p.trim().toLowerCase());

  return files.filter((file) => {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    return patterns.some((pattern) => {
      // Extension pattern: .pdf, .jpg
      if (pattern.startsWith(".")) {
        return fileName.endsWith(pattern);
      }
      // Wildcard MIME: image/*, audio/*
      if (pattern.endsWith("/*")) {
        const prefix = pattern.slice(0, -1);
        return mimeType.startsWith(prefix);
      }
      // Exact MIME: image/png
      return mimeType === pattern;
    });
  });
};

/** Set up drag-and-drop handlers for the drop zone mode. */
const setupDragHandlers = (ctx: UploadContext, isButton: ComputedRef<boolean>, disabled: ComputedRef<boolean>) => {
  const isDragActive = ref(false);
  let dragCounter = 0;

  const handleDragEnter = (e: DragEvent) => {
    if (isButton.value || disabled.value) return;
    e.preventDefault();
    dragCounter++;
    isDragActive.value = true;
  };

  const handleDragOver = (e: DragEvent) => {
    if (isButton.value || disabled.value) return;
    e.preventDefault();
  };

  const handleDragLeave = (e: DragEvent) => {
    if (isButton.value || disabled.value) return;
    e.preventDefault();
    dragCounter--;
    if (dragCounter <= 0) {
      dragCounter = 0;
      isDragActive.value = false;
    }
  };

  const handleDrop = (e: DragEvent) => {
    if (isButton.value || disabled.value) return;
    e.preventDefault();
    dragCounter = 0;
    isDragActive.value = false;

    ctx.invalidFiles.value = [];

    const droppedFiles = e.dataTransfer?.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    const allFiles = [...droppedFiles];
    const filtered = filterByAccept(allFiles, ctx.props.accept);
    const rejected = allFiles.filter((f) => !filtered.includes(f));

    if (rejected.length > 0) rejectFiles(ctx, rejected);
    if (filtered.length === 0) return;

    let value: File | File[];
    if (ctx.props.multiple) {
      const existing = toFileArray(ctx.props.modelValue);
      const newNames = new Set(filtered.map((f) => f.name));
      value = [...existing.filter((f) => !newNames.has(f.name)), ...filtered];
    } else {
      value = filtered[0];
    }
    dispatchUpdate(ctx, value);
    ctx.validate();
  };

  return { isDragActive, handleDragEnter, handleDragOver, handleDragLeave, handleDrop };
};

/** Set up core reactive state and context for the upload composable. */
const setupCore = (props: Readonly<InputUploadProps>, fileInputRef: Ref<HTMLInputElement | null>) => {
  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);
  const { finalColor, finalVariant, finalSize } = useFieldInheritance(host, state);
  const { validate } = setupFormAssociated(fileInputRef, state, props);
  const disabled = computed(() => !!state.disabled?.value);
  const block = computed(() => !!state.block?.value);
  const isButton = computed(() => !!state.button?.value);
  const invalidFiles = ref<File[]>([]);
  const ctx: UploadContext = { props, dispatchEvent, validate, invalidFiles };

  return { host, state, finalColor, finalVariant, finalSize, validate, disabled, block, isButton, ctx };
};

export const useInputUpload = (options: UseInputUploadOptions): UseInputUploadReturn => {
  const { props, fileInputRef } = options;
  const { state, finalColor, finalVariant, finalSize, validate, disabled, block, isButton, ctx } = setupCore(
    props,
    fileInputRef,
  );

  const drag = setupDragHandlers(ctx, isButton, disabled);
  const classes = setupClasses({
    finalColor,
    finalVariant,
    finalSize,
    disabled,
    block,
    isDragActive: drag.isDragActive,
    propClass: props.class,
  });
  const displayText = createDisplayText(state, props.placeholder);
  const hasFiles = createHasFiles(state);
  const fileList = createFileList(state);
  const openFilePicker = () => !disabled.value && fileInputRef.value?.click();

  return {
    state,
    classes,
    finalColor,
    finalVariant,
    finalSize,
    displayText,
    hasFiles,
    isButton,
    isDragActive: drag.isDragActive,
    fileList,
    invalidFiles: ctx.invalidFiles,
    openFilePicker,
    handleFileChange: (e: Event) => handleFileChange(ctx, e),
    handleDragEnter: drag.handleDragEnter,
    handleDragOver: drag.handleDragOver,
    handleDragLeave: drag.handleDragLeave,
    handleDrop: drag.handleDrop,
    removeFile: (file: File) => removeFile(ctx, state, file),
    removeInvalidFile: (file: File) => {
      ctx.invalidFiles.value = ctx.invalidFiles.value.filter((f) => f !== file);
    },
    formatFileSize,
    getFileIcon,
    validate,
    invalidFileCardCls: cardClasses({ color: "error", variant: "soft", size: "xs" }),
    fileCardCls: cardClasses({ color: "neutral", variant: "soft", size: "xs" }),
    getPreviewUrl: (file: File): string | null => (file.type.startsWith("image/") ? URL.createObjectURL(file) : null),
  };
};
